/**
 * 团队管理服务
 * 处理团队相关的API请求
 */

import api from './api';
import endpoints from './endpoints';

const teamService = {
  /**
   * 获取团队详情
   * @param {string} teamId - 团队ID
   * @returns {Promise} - 团队详情数据
   */
  getTeamDetail: async (teamId) => {
    try {
      const response = await api.get(
        endpoints.teams.detail(teamId),
        {},
        'GetTeamDetailResponse'
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 提交入团申请
   * @param {string} teamId - 团队ID
   * @param {Object} applicationData - 申请数据
   * @param {string} applicationData.desired_role - 期望角色
   * @param {string} applicationData.message - 申请理由
   * @returns {Promise} - 申请结果
   */
  submitApplication: async (teamId, applicationData) => {
    try {
      const response = await api.post(
        endpoints.teams.applications.join(teamId),
        applicationData,
        'SubmitApplicationResponse'
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 获取团队列表 (支持指定用户)
   * @param {Object} params - 查询参数
   * @param {number} params.user_id - 用户ID，如果不提供则查询当前登录用户的团队
   * @param {number} params.page - 页码，默认为1
   * @param {number} params.page_size - 每页数量，默认为20
   * @param {boolean} params.recruiting - 是否正在招募，true/false
   * @param {string} params.scenario - 场景筛选
   * @param {string} params.search - 搜索关键词
   * @returns {Promise} - 团队列表数据
   */
  getTeams: async (params = {}) => {
    try {
      const requestParams = {
        page: params.page || 1,
        page_size: params.page_size || 20
      };

      // 添加可选参数
      if (params.user_id) {
        requestParams.user_id = params.user_id;
      }
      if (params.recruiting !== undefined) {
        requestParams.recruiting = params.recruiting;
      }
      if (params.scenario) {
        requestParams.scenario = params.scenario;
      }
      if (params.search) {
        requestParams.search = params.search;
      }

      // 调用新的团队列表接口
      const response = await api.get(
        '/teams',
        { params: requestParams }
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 获取当前用户的团队列表 (向后兼容)
   * @returns {Promise} - 用户团队列表数据
   */
  getUserTeams: async (userId) => {
    try {
      const response = await api.get(
        endpoints.users.me.teams(userId)
      );
      return response;
    } catch (error) {
      return {
        teams: []
      };
    }
  },

  /**
   * 发送团队邀请
   * @param {string} teamId - 团队ID
   * @param {Object} invitationData - 邀请数据
   * @param {string} invitationData.invitee_id - 被邀请人ID
   * @param {string} invitationData.role - 邀请角色
   * @param {string} invitationData.message - 邀请消息
   * @returns {Promise} - 邀请结果
   */
  sendInvitation: async (teamId, invitationData) => {
    try {
      const response = await api.post(
        endpoints.teams.invitations.create(teamId),
        invitationData
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 创建团队
   * @param {Object} teamData - 团队数据
   * @param {string} teamData.name - 团队名称
   * @param {string} teamData.description - 团队描述
   * @param {string} teamData.recruiting_status - 招募状态
   * @param {Object} teamData.team_data - 团队详细数据
   * @param {Array} teamData.initial_members - 初始成员列表
   * @returns {Promise} - 创建结果
   */
  createTeam: async (teamData) => {
    try {
      const response = await api.post(
        endpoints.teams.create,
        teamData
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 解散团队
   * @param {string} teamId - 团队ID
   * @returns {Promise} - 解散结果
   */
  deleteTeam: async (teamId) => {
    try {
      const response = await api.delete(endpoints.teams.delete(teamId));
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 获取团队成员列表
   * @param {string} teamId - 团队ID
   * @returns {Promise} - 成员列表数据
   */
  getTeamMembers: async (teamId) => {
    try {
      const response = await api.get(
        endpoints.teams.members.list(teamId)
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 撤回团队邀请
   * @param {string} teamId - 团队ID
   * @param {string} invitationId - 邀请ID
   * @returns {Promise} - 撤回结果
   */
  revokeInvitation: async (teamId, invitationId) => {
    try {
      const response = await api.delete(
        endpoints.teams.invitations.delete(teamId, invitationId)
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 移除团队成员
   * @param {string} teamId - 团队ID
   * @param {string} userId - 用户ID
   * @returns {Promise} - 移除结果
   */
  removeMember: async (teamId, userId) => {
    try {
      const response = await api.delete(
        endpoints.teams.members.remove(teamId, userId)
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 获取团队提案列表
   * @param {string} teamId - 团队ID
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码，默认1
   * @param {number} params.per_page - 每页数量，默认10
   * @returns {Promise} - 提案列表结果
   */
  getTeamProposals: async (teamId, params = {}) => {
    try {
      // 构建搜索参数，使用POST /api/v2/contents/search
      const searchParams = {
        type: 'article',
        filter: {
          exprs: [
            {
              field: 'category',
              op: 'EQ',
              values: ['competition']
            },
            {
              field: 'group_id',
              op: 'EQ',
              values: [teamId]
            },
            {
              field: 'tags',
              op: 'IN',
              values: ['ai_competition_model']
            }
          ]
        },
        pagination: {
          page: params.page || 1,
          per_page: params.per_page || 10
        },
        sort: {
          field: 'inserted_at',
          desc: true
        }
      };

      const response = await api.post(endpoints.teams.proposals.search, searchParams);
      return response;
    } catch (error) {
      throw error;
    }
  }
};

export default teamService;